import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import {
  FaPlus,
  FaSearch,
  FaList,
  FaEye,
  FaFilter,
  FaTrash,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/hireDetailsListing";

const HireVehicleDetailsListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  // ✅ localStorage values (same style as attached code)
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branchType =
    localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "HO";
  const userId =
    localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const branchActive =
    sessionStorage.getItem("BranchActive") ||
    sessionStorage.getItem("branchActive") ||
    "1";

  // ddl/meta data
  const [branches, setBranches] = useState([]);
  const [stores, setStores] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  // filters
  const [selectedBranchId, setSelectedBranchId] = useState(branchId || "");
  const [storeId, setStoreId] = useState("Select");
  const [vehicleType, setVehicleType] = useState("Select");
  const [hiredFrom, setHiredFrom] = useState("");
  const [vehicleCode, setVehicleCode] = useState("");
  const [vehicleRegNo, setVehicleRegNo] = useState("");
  const [vehicleName, setVehicleName] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // rights
  const [rights, setRights] = useState({
    canAccess: true,
    canAdd: true,
    canDelete: true,
  });

  // selection
  const [selectedRows, setSelectedRows] = useState([]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const normalizeValue = (val) => {
    if (!val || val === "Select") return "";
    return String(val);
  };

  const normalizeRow = (row) => {
    return {
      ...row,
      vehicleDetId:
        row?.fld_vehicledetid ||
        row?.vehicledetid ||
        row?.VehicleDetId ||
        row?.VehicleDetID ||
        0,
      vehicleId:
        row?.fld_vehicleid ||
        row?.vehicleid ||
        row?.VehicleID ||
        0,
      vehicleCode:
        row?.fld_vehicle_code ||
        row?.vehicleCode ||
        row?.VehicleCode ||
        row?.["Vehicle Code"] ||
        "-",
      vehicleName:
        row?.fld_vehicle_name ||
        row?.vehicleName ||
        row?.VehicleName ||
        row?.["Vehicle Name"] ||
        "-",
      vehicleRegNo:
        row?.fld_vehicle_regno ||
        row?.vehicleRegNo ||
        row?.VehicleRegNo ||
        row?.["Vehicle Registration Number"] ||
        "-",
      vehicleSubtype:
        row?.fld_vehicle_subtypename ||
        row?.vehicleSubtype ||
        row?.VehicleSubtype ||
        row?.["Vehicle Subtype"] ||
        "-",
      hiredFrom:
        row?.fld_hiredfrom ||
        row?.hiredFrom ||
        row?.HiredFrom ||
        row?.["Hired From"] ||
        "-",
      basis:
        row?.fld_vehicle_basis ||
        row?.vehicleBasis ||
        row?.VehicleBasis ||
        row?.["Vehicle Basis"] ||
        "-",
      status:
        row?.fld_vehicle_status ||
        row?.vehicleStatus ||
        row?.VehicleStatus ||
        row?.["Vehicle Status"] ||
        "Open",
    };
  };

  // ---------- Rights ----------
  const loadRights = async () => {
    try {
      const r = await axios.get(`${API_BASE}/rights`, {
        params: { loginUserId: userId },
        headers: commonHeaders,
      });

      if (r.data?.success) {
        const data = r.data?.data || {};
        setRights({
          canAccess: data?.rights ?? true,
          canAdd: data?.add ?? true,
          canDelete: data?.del ?? true,
        });
      }
    } catch (err) {
      console.error(err);
      setRights({
        canAccess: true,
        canAdd: true,
        canDelete: true,
      });
    }
  };

  // ---------- Meta ----------
  const loadMeta = async (branchParam) => {
    try {
      const selected =
        branchType === "HO"
          ? branchParam || selectedBranchId || branchId
          : branchId;

      const r = await axios.get(`${API_BASE}/meta`, {
        params: {
          branchId,
          loginUserId: userId,
          branchType,
          selectedBranchId: selected,
        },
        headers: commonHeaders,
      });

      if (!r.data?.success) {
        throw new Error(r.data?.message || "Meta failed");
      }

      const data = r.data?.data || {};
      setBranches(Array.isArray(data?.branches) ? data.branches : []);
      setStores(Array.isArray(data?.stores) ? data.stores : []);
      setVehicleTypes(Array.isArray(data?.vehicleTypes) ? data.vehicleTypes : []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load dropdown data!");
      setBranches([]);
      setStores([]);
      setVehicleTypes([]);
    }
  };

  // ---------- Search ----------
  const loadList = async () => {
    if (!branchId) {
      toast.error("branchId missing in localStorage!");
      return;
    }

    setLoading(true);
    setSelectedRows([]);

    try {
      const selected =
        branchType === "HO"
          ? selectedBranchId || branchId
          : branchId;

      const r = await axios.get(`${API_BASE}/list`, {
        params: {
          mode: "search",
          branchId,
          loginUserId: userId,
          branchType,
          selectedBranchId: selected,
          storeId: normalizeValue(storeId),
          vehicleType: normalizeValue(vehicleType),
          hiredFrom,
          vehicleCode,
          vehicleRegNo,
          vehicleName,
        },
        headers: commonHeaders,
      });

      const data = Array.isArray(r.data?.data) ? r.data.data : [];
      setRows(data.map(normalizeRow));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load list");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------- View All ----------
  const loadViewAll = async () => {
    if (!branchId) {
      toast.error("branchId missing in localStorage!");
      return;
    }

    setLoading(true);
    setSelectedRows([]);

    try {
      const selected =
        branchType === "HO"
          ? selectedBranchId || branchId
          : branchId;

      const r = await axios.get(`${API_BASE}/list`, {
        params: {
          mode: "all",
          branchId,
          loginUserId: userId,
          branchType,
          selectedBranchId: selected,
          storeId: normalizeValue(storeId),
          vehicleType: normalizeValue(vehicleType),
          hiredFrom,
          vehicleCode,
          vehicleRegNo,
          vehicleName,
        },
        headers: commonHeaders,
      });

      const data = Array.isArray(r.data?.data) ? r.data.data : [];
      setRows(data.map(normalizeRow));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load view all list");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Branch change ----------
  const onBranchChange = async (e) => {
    const val = e.target.value;
    setSelectedBranchId(val);
    setStoreId("Select");
    await loadMeta(val);
  };

  // ---------- Selection ----------
  const toggleRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAll =
    rows.length > 0 &&
    selectedRows.length > 0 &&
    selectedRows.length === rows.length;

  const toggleAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      setSelectedRows(rows.map((r) => Number(r.vehicleDetId)).filter(Boolean));
    }
  };

  // ---------- Clear ----------
  const clearFilters = async () => {
    setStoreId("Select");
    setVehicleType("Select");
    setHiredFrom("");
    setVehicleCode("");
    setVehicleRegNo("");
    setVehicleName("");
    toast.info("Filters cleared");
  };

  // ---------- Row action ----------
  const onEdit = (row) => {
    navigate(
      `/vehicle/HireVehicle_DetailsEntry?vehicledetid=${row.vehicleDetId || ""}&vehicleid=${
        row.vehicleId || ""
      }&vehicleCode=${encodeURIComponent(row.vehicleCode || "")}`
    );
  };

  // ---------- Delete ----------
  const onDeleteSelected = async () => {
    if (!rights.canDelete) {
      toast.error("You don't have delete rights.");
      return;
    }

    if (branchActive !== "1") {
      toast.error("Branch is inactive. Delete not allowed.");
      return;
    }

    const ids = selectedRows.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0);

    if (!ids.length) {
      toast.info("Select at least one row to delete.");
      return;
    }

    try {
      const r = await axios.post(
        `${API_BASE}/delete`,
        { vehicledetIds: ids },
        { headers: commonHeaders }
      );

      if (r.data?.success) {
        toast.success(r.data?.message || "Deleted successfully");
        await loadList();
      } else {
        toast.error(r.data?.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  // ---------- Initial load ----------
  useEffect(() => {
    (async () => {
      try {
        await loadRights();
        await loadMeta(selectedBranchId || branchId);
        await loadList();
      } catch (err) {
        console.error(err);
        toast.error("Failed to initialize page");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  if (!rights.canAccess) {
    return (
      <VehicleLayoutWrapper>
        <div className="p-6">
          <div className="p-4 rounded-lg border border-red-300 bg-red-50 text-red-700">
            You don’t have rights to access this page.
          </div>
        </div>
      </VehicleLayoutWrapper>
    );
  }

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">HIRE VEHICLE DETAILS LISTING</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-4">
            <h2 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Hire Vehicle Details Listing
            </h2>

            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}
            >
              {rows.length} Records
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800" +
                    (showFilters ? " bg-gray-800" : "")
                  : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                    (showFilters ? " bg-gray-50" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            {rights.canAdd && branchActive === "1" && (
              <button
                onClick={() => navigate("/vehicle/HireVehicle_DetailsEntry")}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
              >
                <FaPlus className="w-4 h-4" />
                Add New
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div
            className={`rounded-xl p-5 border mb-6 ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Branch */}
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Branch
                </label>
                <select
                  value={selectedBranchId}
                  onChange={onBranchChange}
                  disabled={branchType !== "HO"}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${branchType !== "HO" ? "opacity-70 cursor-not-allowed" : ""}`}
                >
                  <option value="">Select</option>
                  {branches.map((b, idx) => {
                    const id =
                      b?.fld_Branchid ||
                      b?.BranchId ||
                      b?.branchid ||
                      b?.fld_branchid ||
                      "";
                    const name =
                      b?.fld_BranchName ||
                      b?.BranchName ||
                      b?.branchname ||
                      id;
                    return (
                      <option key={idx} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Store */}
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Store
                </label>
                <select
                  value={storeId}
                  onChange={(e) => setStoreId(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="Select">Select</option>
                  {stores.map((s, idx) => {
                    const id = s?.fld_storeid || s?.StoreId || s?.storeid || "";
                    const name = s?.fld_storename || s?.StoreName || s?.storename || id;
                    return (
                      <option key={idx} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Vehicle Subtype */}
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Vehicle Subtype
                </label>
                <select
                  value={vehicleType}
                  onChange={(e) => setVehicleType(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="Select">Select</option>
                  {vehicleTypes.map((v, idx) => {
                    const id =
                      v?.fld_vehicle_subtypeid ||
                      v?.VehicleSubtypeId ||
                      v?.subtypeid ||
                      v?.id ||
                      "";
                    const name =
                      v?.fld_vehicle_subtypename ||
                      v?.VehicleSubtypeName ||
                      v?.subtypename ||
                      v?.name ||
                      id;
                    return (
                      <option key={idx} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Hired From */}
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Hired From
                </label>
                <input
                  type="text"
                  value={hiredFrom}
                  onChange={(e) => setHiredFrom(e.target.value)}
                  placeholder="Hired From"
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
              </div>

              {/* Vehicle Code */}
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Vehicle Code
                </label>
                <input
                  type="text"
                  value={vehicleCode}
                  onChange={(e) => setVehicleCode(e.target.value)}
                  placeholder="Vehicle Code"
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
              </div>

              {/* Vehicle Reg No */}
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Vehicle Reg No
                </label>
                <input
                  type="text"
                  value={vehicleRegNo}
                  onChange={(e) => setVehicleRegNo(e.target.value)}
                  placeholder="Vehicle Reg No"
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
              </div>

              {/* Vehicle Name */}
              <div>
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Vehicle Name
                </label>
                <input
                  type="text"
                  value={vehicleName}
                  onChange={(e) => setVehicleName(e.target.value)}
                  placeholder="Vehicle Name"
                  className={`w-full px-4 py-2 rounded-lg border ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
              </div>

              {/* Buttons */}
              <div className="flex items-end gap-3 flex-wrap">
                <button
                  onClick={loadList}
                  className={`flex items-center gap-2 px-4 py-2 rounded text-white ${buttonClass} ${hoverClass}`}
                >
                  <FaSearch /> Search
                </button>

                <button
                  onClick={loadViewAll}
                  className="flex items-center gap-2 px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <FaList /> View All
                </button>

                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk action */}
        {selectedRows.length > 0 && rights.canDelete && branchActive === "1" && (
          <div
            className={`rounded-lg p-4 mb-6 ${
              isDark ? "bg-blue-900/30 border border-blue-700" : "bg-blue-50 border border-blue-200"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className={`font-semibold ${isDark ? "text-blue-200" : "text-blue-800"}`}>
                {selectedRows.length} vehicle(s) selected
              </div>

              <button
                onClick={onDeleteSelected}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <FaTrash />
                Delete Selected
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div
          className={`rounded-xl shadow-sm border overflow-hidden ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className={`${buttonClass} text-white`}>
                <tr>
                  <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">S.No</th>
                  <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">
                    Vehicle Code
                  </th>
                  <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">
                    Vehicle Name
                  </th>
                  <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">
                    Vehicle Reg No
                  </th>
                  <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">
                    Vehicle Subtype
                  </th>
                  <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">
                    Hired From
                  </th>
                  <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">
                    Basis
                  </th>
                  <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">
                    Status
                  </th>
                  <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">
                    Action
                  </th>
                  <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={toggleAll}
                      className="mr-2"
                    />
                    Select All
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={idx} className={isDark ? "bg-gray-800" : "bg-white"}>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-10" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-28" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-40" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-32" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-32" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-32" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-20" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-6 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-20" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-20" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 w-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse mx-auto" />
                      </td>
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                      No records found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => (
                    <tr
                      key={row.vehicleDetId || idx}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 ${
                        isDark ? "bg-gray-900" : "bg-white"
                      }`}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {idx + 1}
                      </td>

                      <td className="px-4 py-3">
                        <span className="text-blue-600 dark:text-blue-400 font-medium">
                          {row.vehicleCode}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-gray-900 dark:text-white">
                        {row.vehicleName}
                      </td>

                      <td className="px-4 py-3 text-gray-900 dark:text-white">
                        {row.vehicleRegNo}
                      </td>

                      <td className="px-4 py-3 text-gray-900 dark:text-white">
                        {row.vehicleSubtype}
                      </td>

                      <td className="px-4 py-3 text-gray-900 dark:text-white">
                        {row.hiredFrom}
                      </td>

                      <td className="px-4 py-3 text-gray-900 dark:text-white">
                        {row.basis}
                      </td>

                      <td className="px-4 py-3 text-gray-900 dark:text-white">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            String(row.status).toLowerCase() === "open"
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
                              : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100"
                          }`}
                        >
                          {row.status}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <button
                          onClick={() => onEdit(row)}
                          disabled={!(rights.canAdd && branchActive === "1")}
                          className={`px-3 py-1 rounded text-xs font-medium ${
                            isDark
                              ? "bg-blue-600 hover:bg-blue-700 text-white"
                              : "bg-blue-100 hover:bg-blue-200 text-blue-700"
                          } ${
                            !(rights.canAdd && branchActive === "1")
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                        >
                          <FaEye className="inline mr-1" />
                          Edit
                        </button>
                      </td>

                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(Number(row.vehicleDetId))}
                          onChange={() => toggleRow(Number(row.vehicleDetId))}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default HireVehicleDetailsListing;